import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Fuel, AlertTriangle } from 'lucide-react';

const FuelEfficiencyDisplay = ({ fuelEfficiency }) => {
  if (!fuelEfficiency) return null;
  const isWarning = fuelEfficiency.isAbnormal || fuelEfficiency.hasVariation;
  const bgClass = isWarning ? 'bg-red-100 border border-red-300' : 'bg-green-100 border border-green-300';
  const textClass = isWarning ? 'text-red-800' : 'text-green-800';
  const subClass = isWarning ? 'text-red-700' : 'text-green-700';

  return (
    <div className={`p-3 rounded-lg ${bgClass}`}>
      <div className="flex items-start gap-2">
        {isWarning && <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />}
        <div className="flex-1">
          <p className={`text-lg font-bold ${textClass}`}>{fuelEfficiency.kmPerLiter} km/L</p>
          <p className={`text-sm ${subClass}`}>({fuelEfficiency.lPer100km} L/100km)</p>
          <p className="text-xs text-gray-600 mt-1">
            {fuelEfficiency.kmDriven} km driven using {fuelEfficiency.litersUsed} L since last fill
          </p>
          {fuelEfficiency.vehicleAvgKmL && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                Vehicle Average: <span className="font-semibold">{fuelEfficiency.vehicleAvgKmL} km/L</span>
              </p>
              {fuelEfficiency.hasVariation && (
                <p className="text-sm text-red-700 font-bold mt-1">
                  {Math.abs(parseFloat(fuelEfficiency.variationPercent)).toFixed(1)}% {parseFloat(fuelEfficiency.variationPercent) > 0 ? 'BETTER' : 'WORSE'} than average
                </p>
              )}
              {!fuelEfficiency.hasVariation && fuelEfficiency.variationPercent && (
                <p className="text-xs text-green-600 mt-1">
                  Within normal range ({fuelEfficiency.variationPercent > 0 ? '+' : ''}{fuelEfficiency.variationPercent}%)
                </p>
              )}
            </div>
          )}
          {fuelEfficiency.isAbnormal && (
            <p className="text-xs text-red-700 mt-2 font-medium bg-red-50 p-2 rounded">
              Extreme fuel consumption detected. Please verify odometer and liter readings.
            </p>
          )}
          {fuelEfficiency.hasVariation && parseFloat(fuelEfficiency.variationPercent) < -10 && (
            <p className="text-xs text-red-700 mt-2 font-medium bg-red-50 p-2 rounded">
              Fuel usage significantly higher than average. Possible causes: fuel theft, vehicle issue, or data entry error.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};


export const FuelEntrySection = ({
  formData, updateField, setFormData, isHoursBased, isMeterExempt,
  lastFuelEntry, lastVehicleOdo, fuelEfficiency,
  suppliers, getRelevantSuppliers,
  suggestion, onApplySuggestion,
}) => {
  // Calculate expected litres between transactions
  const expectedLitres = (() => {
    if (!lastFuelEntry?.odometer || !formData.odometer || isHoursBased) return null;
    const kmDriven = parseFloat(formData.odometer) - lastFuelEntry.odometer;
    if (kmDriven <= 0) return null;
    const avgKmL = fuelEfficiency?.vehicleAvgKmL ? parseFloat(fuelEfficiency.vehicleAvgKmL) : null;
    if (!avgKmL || avgKmL <= 0) return null;
    const expected = kmDriven / avgKmL;
    const actual = formData.liters ? parseFloat(formData.liters) : null;
    const diff = actual ? actual - expected : null;
    const diffPct = (actual && expected > 0) ? ((diff / expected) * 100) : null;
    return { kmDriven, expected, actual, diff, diffPct, avgKmL };
  })();

  return (
  <div className="border-t border-b py-4 my-4 bg-orange-50/50 -mx-6 px-6">
    <h4 className="font-medium text-sm text-orange-800 mb-3 flex items-center gap-2">
      <Fuel className="w-4 h-4" />
      Fuel Entry Details
    </h4>

    {/* Usage Reading */}
    <div className="space-y-2 mb-4">
      <Label className="text-orange-900">
        {isHoursBased
          ? (isMeterExempt ? 'Hours Reading (optional)' : 'Hours Reading *')
          : (isMeterExempt ? 'Odometer Reading (km) — optional' : 'Odometer Reading (km) *')}
      </Label>
      {isHoursBased ? (
        <Input type="number" step="0.1" min="0" value={formData.hours}
          onChange={(e) => updateField('hours', e.target.value)}
          placeholder="Current hours reading" className="font-data border-orange-200 focus:border-orange-400"
          data-testid="cost-hours-input" required={!isMeterExempt} />
      ) : (
        <Input type="number" step="1" min="0" value={formData.odometer}
          onChange={(e) => updateField('odometer', e.target.value)}
          placeholder="Current odometer reading" className="font-data border-orange-200 focus:border-orange-400"
          data-testid="cost-odometer-input" required={!isMeterExempt} />
      )}
      {isMeterExempt && (
        <p className="text-xs text-slate-600 italic" data-testid="meter-exempt-hint">
          This vehicle is excluded from the fleet average — no meter reading needed.
        </p>
      )}
      {!isMeterExempt && suggestion && (
        <div
          className="flex items-center justify-between gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800"
          data-testid="usage-suggestion-banner"
        >
          <span>
            Suggested {isHoursBased ? 'hours' : 'odometer'}:{' '}
            <b className="font-data">{Number(suggestion.value).toLocaleString('en-GB')}</b>{' '}
            <span className="text-blue-600">({suggestion.source.replace('_', ' ')})</span>
          </span>
          <button
            type="button"
            onClick={onApplySuggestion}
            className="text-blue-700 hover:text-blue-900 font-semibold underline"
            data-testid="apply-suggestion-btn"
          >
            Use this value
          </button>
        </div>
      )}
      {/* Inline required-field alert — reinforces the toast so operators catch the miss BEFORE hitting Save */}
      {!isMeterExempt && ((isHoursBased && !formData.hours) || (!isHoursBased && !formData.odometer)) && (
        <div
          className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800"
          data-testid="required-meter-alert"
        >
          <b>{isHoursBased ? 'Hours' : 'Odometer'} is required.</b> Every fuel entry needs a meter reading so we can calculate {isHoursBased ? 'L/hr' : 'km/L'} correctly.
        </div>
      )}
      {lastVehicleOdo?.odometer && !isHoursBased && (
        <p className="text-xs text-red-600 font-medium" data-testid="prev-odo-fuel">
          Previous: {lastVehicleOdo.odometer.toLocaleString()} km ({lastVehicleOdo.date}) | {(lastVehicleOdo.cost_type || '').toUpperCase()}
          {lastVehicleOdo.liters ? ` | ${lastVehicleOdo.liters}L` : ''}
          {lastVehicleOdo.amount ? ` | R ${Number(lastVehicleOdo.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}
        </p>
      )}
      {!lastVehicleOdo?.odometer && lastFuelEntry?.odometer && !isHoursBased && (
        <p className="text-xs text-red-600 font-medium" data-testid="prev-odo-fuel">
          Previous: {lastFuelEntry.odometer.toLocaleString()} km ({lastFuelEntry.date})
          {lastFuelEntry.liters ? ` | ${lastFuelEntry.liters}L` : ''}
          {lastFuelEntry.amount ? ` | R ${Number(lastFuelEntry.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}
        </p>
      )}
      {(() => {
        const lastOdo = lastVehicleOdo?.odometer || lastFuelEntry?.odometer;
        return lastOdo && !isHoursBased && formData.odometer && parseFloat(formData.odometer) < lastOdo ? (
          <div className="p-2 bg-red-100 border border-red-400 rounded text-xs text-red-800 font-bold" data-testid="fuel-odo-warning">
            Odometer {parseFloat(formData.odometer).toLocaleString()} km is LOWER than last recorded {lastOdo.toLocaleString()} km — please verify
          </div>
        ) : null;
      })()}
      {lastVehicleOdo?.hours && isHoursBased && (
        <p className="text-xs text-red-600 font-medium" data-testid="prev-hours-fuel">
          Previous: {lastVehicleOdo.hours.toLocaleString()} hrs ({lastVehicleOdo.date}) | {(lastVehicleOdo.cost_type || '').toUpperCase()}
          {lastVehicleOdo.liters ? ` | ${lastVehicleOdo.liters}L` : ''}
          {lastVehicleOdo.amount ? ` | R ${Number(lastVehicleOdo.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}
        </p>
      )}
      {expectedLitres && (
        <div className={`mt-1 p-2 rounded text-xs font-medium ${
          expectedLitres.diff !== null && expectedLitres.diffPct > 20
            ? 'bg-red-100 border border-red-300 text-red-800'
            : 'bg-slate-100 border border-slate-200 text-slate-700'
        }`} data-testid="expected-litres-fuel">
          <span>Avg expected: {expectedLitres.expected.toFixed(1)}L for {expectedLitres.kmDriven.toFixed(0)} km (avg {expectedLitres.avgKmL.toFixed(1)} km/L)</span>
          {expectedLitres.actual !== null && (
            <span className="ml-1">
              — Actual: {expectedLitres.actual.toFixed(1)}L
              {expectedLitres.diffPct !== null && (
                <span className={expectedLitres.diffPct > 20 ? ' font-bold text-red-700' : ''}>
                  {' '}({expectedLitres.diff > 0 ? '+' : ''}{expectedLitres.diff.toFixed(1)}L / {expectedLitres.diffPct > 0 ? '+' : ''}{expectedLitres.diffPct.toFixed(0)}%)
                </span>
              )}
            </span>
          )}
          {expectedLitres.diff !== null && expectedLitres.diffPct > 20 && (
            <p className="mt-1 font-bold">Warning: Actual litres {expectedLitres.diffPct.toFixed(0)}% above expected — check for fuel loss or theft</p>
          )}
        </div>
      )}
    </div>

    {/* Invoice Section */}
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <h5 className="font-medium text-blue-900 mb-3">Enter from Supplier Invoice</h5>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-blue-900 text-sm">Liters *</Label>
          <Input type="number" step="any" min="0" value={formData.liters}
            onChange={(e) => updateField('liters', e.target.value)}
            placeholder="161.539" className="font-data border-blue-200 bg-white"
            data-testid="cost-liters-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-blue-900 text-sm">Total Cost (R) *</Label>
          <Input type="number" step="0.01" min="0" value={formData.amount}
            onChange={(e) => updateField('amount', e.target.value)}
            placeholder="4000.00" className="font-data border-blue-200 bg-white font-bold"
            data-testid="cost-amount-input" />
        </div>
      </div>
      {formData.liters && formData.amount && parseFloat(formData.liters) > 0 && (
        <p className="text-xs text-blue-700 mt-2 font-data" data-testid="fuel-derived-price">
          Auto-calculated price/litre: R {(parseFloat(formData.amount) / parseFloat(formData.liters)).toFixed(3)}
        </p>
      )}
      <p className="text-xs text-blue-600 mt-1">Enter litres and total cost exactly as shown on the invoice. Price/litre is calculated automatically.</p>
    </div>

    {/* Fuel Efficiency */}
    <FuelEfficiencyDisplay fuelEfficiency={fuelEfficiency} />

    {/* Supplier and Invoice */}
    <div className="grid grid-cols-2 gap-4 mt-4">
      <div className="space-y-2">
        <Label className="text-orange-900">Supplier *</Label>
        {suppliers.length > 0 ? (
          <Select value={formData.supplier_id}
            onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}>
            <SelectTrigger data-testid="cost-supplier-select" className="border-orange-200">
              <SelectValue placeholder="Select supplier" />
            </SelectTrigger>
            <SelectContent>
              {getRelevantSuppliers().map((supplier) => (
                <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input value={formData.vendor}
            onChange={(e) => updateField('vendor', e.target.value)}
            placeholder="e.g., Shell, BP, Engen" className="border-orange-200"
            data-testid="cost-vendor-input" />
        )}
        {suppliers.length === 0 && (
          <p className="text-xs text-orange-600">
            <a href="/suppliers" className="underline">Add suppliers</a> to use dropdown
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label className="text-orange-900">Invoice Number</Label>
        <Input value={formData.invoice_number}
          onChange={(e) => updateField('invoice_number', e.target.value)}
          placeholder="INV-00001" className="font-data border-orange-200"
          data-testid="cost-invoice-input" />
      </div>
      <div className="space-y-2">
        <Label className="text-orange-900">Order Nr</Label>
        <Input value={formData.order_number || ''}
          onChange={(e) => updateField('order_number', e.target.value)}
          placeholder="PO-00001" className="font-data border-orange-200"
          data-testid="cost-order-input" />
      </div>
    </div>
  </div>
  );
};
